/**
 * ChatPaneCard — the chat side of the split as a slot-shell, extracted
 * verbatim-modulo-props from ChatBox.tsx (Wave B.2 commit 2).
 *
 * SLOT-SHELL form on purpose: a flat extraction of this JSX range would need
 * ~115 distinct props (MessageList carries 54 bindings, ChatComposer 47), so
 * ChatBox keeps BUILDING the <MessageList/> and <ChatComposer/> elements and
 * passes them down as the `messageList` / `composer` ReactNode slots. Only the
 * chrome moved here: the dock close button, the quota warn banner, the
 * messages shell, the import-progress indicator, and the bottom stack (mobile
 * module chip tray + the composer mount gate). All class names, conditions,
 * and handler bodies are byte-identical to the original inline JSX; the only
 * changes are reads-through-props (e.g. `onCloseModule` for `hideModulePane`,
 * `onOpenMobileModule` for `() => setMobileView('module')`) and the
 * precomputed `composerVisible` gate (ChatBox evaluates the original
 * expression at the call site).
 *
 * Pinned by ChatBox.render.test.tsx (CN-1 shell classes, CN-2 composer
 * placeholders, CN-6 quota banner + dismiss, CN-9 close persist).
 */
import type { ReactNode, RefObject } from 'react';
import { UsageWarnBanner, type QuotaWindow } from '@/common/components/UsageWarnBanner';
import type { ImportPhase, ImportFileStatus } from '@/features/chat/hooks/useImport';
import { getModuleMeta, type Module } from '@/types/modules';
import { ImportProgressIndicator } from './ImportProgressIndicator';
import { QUOTA_WARN_DISMISS_KEY } from './constants';

export interface ChatPaneCardProps {
  // Dock chrome (close button)
  isChatDockSplitActive: boolean;
  chatDockState: 'open' | 'docked' | 'docking' | 'undocking';
  handleDockChat: () => void;
  // Quota warn banner
  showQuotaWarnBanner: boolean;
  quotaWarnPct: number;
  quotaWarnWindow: QuotaWindow;
  setQuotaWarnDismissed: (dismissed: boolean) => void;
  // Messages shell
  messagesContainerRef: RefObject<HTMLDivElement | null>;
  compactMode: boolean;
  isScrollSwitchSettling: boolean;
  isEmptyChat: boolean;
  // Import progress indicator
  isImporting: boolean;
  importPhase: ImportPhase;
  importProgress: { imported: number; total: number };
  importingFiles: ImportFileStatus[];
  // Bottom stack
  bottomStackRef: RefObject<HTMLDivElement | null>;
  // Mobile module chip tray
  isMobileChatWithModule: boolean;
  selectedChatPaneModule: Module | null;
  selectedChatPaneModuleTitle: string;
  onOpenMobileModule: () => void;
  onCloseModule: () => void;
  /** The original composer mount gate, precomputed at the ChatBox call site. */
  composerVisible: boolean;
  // Slots — built in ChatBox so their ~100 bindings never become props.
  messageList: ReactNode;
  composer: ReactNode;
}

export function ChatPaneCard({
  isChatDockSplitActive,
  chatDockState,
  handleDockChat,
  showQuotaWarnBanner,
  quotaWarnPct,
  quotaWarnWindow,
  setQuotaWarnDismissed,
  messagesContainerRef,
  compactMode,
  isScrollSwitchSettling,
  isEmptyChat,
  isImporting,
  importPhase,
  importProgress,
  importingFiles,
  bottomStackRef,
  isMobileChatWithModule,
  selectedChatPaneModule,
  selectedChatPaneModuleTitle,
  onOpenMobileModule,
  onCloseModule,
  composerVisible,
  messageList,
  composer,
}: ChatPaneCardProps) {
  return (
    <div className="chat-pane-card">
      <div className="chat-content-shell" id="chat-dock-panel">
      <>
      {isChatDockSplitActive && (chatDockState === 'open' || chatDockState === 'docking') && (
        <button
          type="button"
          className="chat-side-close-btn"
          title="Close chat"
          aria-label="Close chat panel"
          onClick={handleDockChat}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6L6 18" /><path d="M6 6l12 12" />
          </svg>
        </button>
      )}
      {showQuotaWarnBanner && (
        <UsageWarnBanner
          pct={quotaWarnPct}
          window={quotaWarnWindow}
          onUpgrade={() => {
            window.dispatchEvent(new CustomEvent('xavior:open-upgrade-modal'));
          }}
          onDismiss={() => {
            try {
              sessionStorage.setItem(QUOTA_WARN_DISMISS_KEY, 'true');
            } catch {
              // best-effort — banner stays hidden via in-memory state
            }
            setQuotaWarnDismissed(true);
          }}
        />
      )}
      <div
        ref={messagesContainerRef}
        className={`chat-messages xv-scroll ${compactMode ? 'compact' : ''} ${isScrollSwitchSettling && !isEmptyChat ? 'chat-messages-suppress' : ''}`}
        // While suppressed (opacity:0 during the switch re-pin) the messages are
        // invisible but otherwise live; `inert` removes them from the tab order +
        // a11y tree + pointer interaction so a keyboard/AT user can't land on
        // invisible content. Cleared the instant the suppress window ends.
        inert={isScrollSwitchSettling && !isEmptyChat}
      >

        {messageList}
      </div>
      </>
      </div>

      {isImporting && (
        <ImportProgressIndicator
          phase={importPhase}
          progress={importProgress}
          files={importingFiles}
        />
      )}

      <div className="chat-bottom-stack" ref={bottomStackRef}>
        {isMobileChatWithModule && (
          <div className="chat-chip-tray">
            <button
              type="button"
              className="chat-module-chip"
              onClick={onOpenMobileModule}
            >
              <span className="chat-module-chip-icon">
                {(() => {
                  const meta = getModuleMeta(selectedChatPaneModule?.type as any);
                  return meta?.icon || '📄';
                })()}
              </span>
              <span className="chat-module-chip-label">
                {selectedChatPaneModuleTitle || 'Module'}
              </span>
              <span
                className="chat-module-chip-dismiss"
                onClick={(e) => {
                  e.stopPropagation();
                  onCloseModule();
                }}
                role="button"
                tabIndex={0}
                aria-label="Close module"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18" /><path d="M6 6l12 12" />
                </svg>
              </span>
            </button>
          </div>
        )}
        {/* Chat Input — hide on mobile when a module pane is the active view (the
            module owns the screen; the composer returns in chat view), and also
            when the channels module is active (it has its own input). */}
        {composerVisible && composer}
      </div>
    </div>
  );
}
